/**
 * @fileoverview Pure immutable widget operations for `DashboardCanvasBuilder`
 * (`addWidget`, `moveWidget`, `duplicateWidget`, `removeWidget`, `setWidgetEnabled`,
 * `applyLayoutChange`, `normalizeDashboardConfig`).
 * @module ui-admin/components/DashboardCanvasBuilder/dashboardCanvasDragUtils
 *
 * Responsibilities: - Provide pure, non-mutating transformations on `DashboardConfig`.
 *                   - Normalize legacy flat widget coordinates (`x, y, w, h` on root widget)
 *                     and untyped `widget.config` into canonical `DashboardWidget` sub-configs.
 * Dependencies:     - @firesquirrel/local-db (DashboardConfig, DashboardWidget)
 *                   - ./widgetTypes (WIDGET_TYPES)
 *                   - ./dashboardDefaults (DEFAULT_DASHBOARD_LAYOUT)
 * Architecture:     - Pure functions; zero side effects, zero Dexie/API calls.
 * Notes:            - Matches `05-dashboard-canvas-builder-spec.md` §7 and `12-test-plan.md` §2.
 */

import type { DashboardConfig, DashboardWidget } from '@firesquirrel/local-db';
import { WIDGET_TYPES } from './widgetTypes';
import { DEFAULT_DASHBOARD_LAYOUT } from './dashboardDefaults';

/**
 * @contract RglLayoutItem
 * Purpose: Minimal `react-grid-layout` layout item shape used by `applyLayoutChange`.
 * Fields:  i, x, y, w, h
 * Rules:   `i` corresponds to `widget.id`.
 * Sync:    N/A.
 */
export interface RglLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

function makeWidgetId(): string {
  return `widget_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Normalize a potentially legacy or AI-generated `DashboardConfig` so every widget has:
 * - nested `layout: { x, y, w, h, minW, minH }` (even if legacy flat `x, y, w, h` was stored)
 * - `enabled: boolean` (default `true`)
 * - typed sub-configs (`chartConfig`, `kpiConfig`, `filterConfig`, `textConfig`) populated from
 *   legacy `widget.config` when absent.
 *
 * @process normalizeDashboardConfig
 * Steps:   1. Ensure `config.layout` has `columns` and `rowHeight`.
 *          2. Map each widget to a normalized clone without mutating the input.
 * Inputs:  rawConfig (DashboardConfig | undefined).
 * Outputs: Normalized `DashboardConfig`.
 * Failure Handling: Returns default config when `rawConfig` is null/undefined.
 * Idempotency: REQUIRED — pure function.
 */
export function normalizeDashboardConfig(rawConfig?: DashboardConfig | null): DashboardConfig {
  if (!rawConfig) {
    return {
      layout: { ...DEFAULT_DASHBOARD_LAYOUT },
      widgets: [],
      autoRefreshSeconds: 0,
    };
  }

  const widgets: DashboardWidget[] = (rawConfig.widgets ?? []).map((rawWidget, idx) => {
    const anyW = rawWidget as any;
    const typeOpt = WIDGET_TYPES.find((t) => t.type === rawWidget.type);
    const defaultW = typeOpt?.defaultW ?? 4;
    const defaultH = typeOpt?.defaultH ?? 2;

    const layout = rawWidget.layout
      ? {
          x: rawWidget.layout.x ?? (idx * 4) % 12,
          y: rawWidget.layout.y ?? Math.floor((idx * 4) / 12) * 2,
          w: rawWidget.layout.w ?? defaultW,
          h: rawWidget.layout.h ?? defaultH,
          minW: rawWidget.layout.minW ?? typeOpt?.minW ?? 2,
          minH: rawWidget.layout.minH ?? typeOpt?.minH ?? 1,
        }
      : {
          x: typeof anyW.x === 'number' ? anyW.x : (idx * 4) % 12,
          y: typeof anyW.y === 'number' ? anyW.y : Math.floor((idx * 4) / 12) * 2,
          w: typeof anyW.w === 'number' ? anyW.w : defaultW,
          h: typeof anyW.h === 'number' ? anyW.h : defaultH,
          minW: typeOpt?.minW ?? 2,
          minH: typeOpt?.minH ?? 1,
        };

    const legacyCfg = anyW.config ?? {};
    const chartConfig =
      rawWidget.chartConfig ??
      (rawWidget.type === 'chart'
        ? {
            type: legacyCfg.chartType ?? 'bar',
            showLegend: legacyCfg.showLegend ?? true,
            showLabels: legacyCfg.showLabels ?? false,
          }
        : undefined);

    const kpiConfig =
      rawWidget.kpiConfig ??
      (rawWidget.type === 'kpi'
        ? {
            measure: rawWidget.dataSource?.measures?.[0] ?? 'InspectionSeries.count',
            format: 'number' as const,
            trendDirection: 'up-good' as const,
          }
        : undefined);

    const textConfig =
      rawWidget.textConfig ??
      (rawWidget.type === 'text' && typeof legacyCfg.content === 'string'
        ? { content: legacyCfg.content }
        : undefined);

    const filterConfig =
      rawWidget.filterConfig ??
      (rawWidget.type === 'filter'
        ? {
            targetWidgets: [],
            field: legacyCfg.field ?? 'InspectionSeries.status',
            label: legacyCfg.label ?? rawWidget.title ?? 'Filter',
            type:
              legacyCfg.filterType === 'dateRange'
                ? ('dateRange' as const)
                : legacyCfg.filterType === 'search'
                  ? ('text' as const)
                  : ('select' as const),
          }
        : undefined);

    return {
      ...rawWidget,
      id: rawWidget.id || `widget_${idx}`,
      enabled: rawWidget.enabled ?? true,
      layout,
      ...(chartConfig ? { chartConfig } : {}),
      ...(kpiConfig ? { kpiConfig } : {}),
      ...(textConfig ? { textConfig } : {}),
      ...(filterConfig ? { filterConfig } : {}),
    };
  });

  return {
    ...rawConfig,
    layout: {
      columns: rawConfig.layout?.columns ?? DEFAULT_DASHBOARD_LAYOUT.columns,
      rowHeight: rawConfig.layout?.rowHeight ?? DEFAULT_DASHBOARD_LAYOUT.rowHeight,
      gap: rawConfig.layout?.gap ?? DEFAULT_DASHBOARD_LAYOUT.gap,
      cardRadius: rawConfig.layout?.cardRadius ?? DEFAULT_DASHBOARD_LAYOUT.cardRadius,
      showHeaderBanner:
        rawConfig.layout?.showHeaderBanner ?? DEFAULT_DASHBOARD_LAYOUT.showHeaderBanner,
    },
    widgets,
  };
}

/**
 * Add a widget of the given type at the next available grid position.
 *
 * @process addWidget
 * Inputs:  config, type.
 * Outputs: New `DashboardConfig` with the added widget appended.
 * Idempotency: NOT REQUIRED (generates a unique widget id).
 */
export function addWidget(
  config: DashboardConfig,
  type: DashboardWidget['type'],
): DashboardConfig {
  const typeOpt = WIDGET_TYPES.find((t) => t.type === type);
  const w = typeOpt?.defaultW ?? 4;
  const h = typeOpt?.defaultH ?? 2;
  const minW = typeOpt?.minW ?? 2;
  const minH = typeOpt?.minH ?? 1;

  const existing = config.widgets ?? [];
  const maxBottom = existing.reduce(
    (max, item) => Math.max(max, (item.layout?.y ?? 0) + (item.layout?.h ?? 2)),
    0,
  );
  const nextX = (existing.length * w) % (config.layout?.columns || 12);
  const nextY = existing.length === 0 ? 0 : maxBottom;

  const defaultDataSource =
    type === 'kpi' || type === 'chart' || type === 'table' || type === 'list' || type === 'gauge' || type === 'progressBar'
      ? {
          cubeId: 'InspectionSeries',
          measures: ['InspectionSeries.count'],
          dimensions: type === 'chart' || type === 'table' ? ['InspectionSeries.status'] : [],
          filters: [],
        }
      : undefined;

  const newWidget: DashboardWidget = {
    id: makeWidgetId(),
    type,
    title: `New ${typeOpt?.label ?? type}`,
    enabled: true,
    layout: { x: nextX, y: nextY, w, h, minW, minH },
    ...(defaultDataSource ? { dataSource: defaultDataSource } : {}),
    ...(type === 'chart'
      ? { chartConfig: { type: 'bar', showLegend: true, showLabels: false } }
      : {}),
    ...(type === 'kpi'
      ? {
          kpiConfig: {
            measure: 'InspectionSeries.count',
            format: 'number',
            trendDirection: 'up-good',
          },
        }
      : {}),
    ...(type === 'text' ? { textConfig: { content: 'Add text content...' } } : {}),
    ...(type === 'filter'
      ? {
          filterConfig: {
            targetWidgets: [],
            field: 'InspectionSeries.status',
            label: 'Status',
            type: 'select',
          },
        }
      : {}),
    ...(type === 'progressBar'
      ? {
          progressBarConfig: {
            currentMeasure: 'InspectionSeries.count',
            targetValue: 100,
            format: 'number',
            showLabel: true,
          },
        }
      : {}),
  };

  return {
    ...config,
    widgets: [...existing.map((item) => ({ ...item, layout: { ...item.layout } })), newWidget],
  };
}

/**
 * Move a widget up (`dir = -1`) or down (`dir = 1`) in `config.widgets` and swap their `y` positions.
 *
 * @process moveWidget
 * Inputs:  config, id, dir (-1 | 1).
 * Outputs: New `DashboardConfig`.
 * Idempotency: NOT REQUIRED.
 */
export function moveWidget(
  config: DashboardConfig,
  id: string,
  dir: -1 | 1,
): DashboardConfig {
  const list = config.widgets.map((w) => ({ ...w, layout: { ...w.layout } }));
  const idx = list.findIndex((w) => w.id === id);
  if (idx < 0) return { ...config, widgets: list };
  const targetIdx = idx + dir;
  if (targetIdx < 0 || targetIdx >= list.length) return { ...config, widgets: list };

  const currentY = list[idx].layout.y;
  list[idx].layout.y = list[targetIdx].layout.y;
  list[targetIdx].layout.y = currentY;

  const [moved] = list.splice(idx, 1);
  list.splice(targetIdx, 0, moved);

  return {
    ...config,
    widgets: list,
  };
}

/**
 * Duplicate a widget by id (creates a copy with a new id and offset `y` position).
 *
 * @process duplicateWidget
 * Inputs:  config, id.
 * Outputs: New `DashboardConfig`.
 */
export function duplicateWidget(config: DashboardConfig, id: string): DashboardConfig {
  const list = config.widgets.map((w) => ({ ...w, layout: { ...w.layout } }));
  const idx = list.findIndex((w) => w.id === id);
  if (idx < 0) return { ...config, widgets: list };

  const source = list[idx];
  const copy: DashboardWidget = {
    ...source,
    id: makeWidgetId(),
    title: `${source.title} (Copy)`,
    layout: {
      ...source.layout,
      y: source.layout.y + source.layout.h,
    },
  };

  const next = [...list];
  next.splice(idx + 1, 0, copy);
  return {
    ...config,
    widgets: next,
  };
}

/**
 * Remove a widget by id.
 *
 * @process removeWidget
 * Inputs:  config, id.
 * Outputs: New `DashboardConfig` without the target widget.
 */
export function removeWidget(config: DashboardConfig, id: string): DashboardConfig {
  return {
    ...config,
    widgets: config.widgets
      .filter((w) => w.id !== id)
      .map((w) => ({ ...w, layout: { ...w.layout } })),
  };
}

/**
 * Toggle a widget's `enabled` flag.
 *
 * @process setWidgetEnabled
 * Inputs:  config, id, enabled.
 * Outputs: New `DashboardConfig`.
 */
export function setWidgetEnabled(
  config: DashboardConfig,
  id: string,
  enabled: boolean,
): DashboardConfig {
  return {
    ...config,
    widgets: config.widgets.map((w) =>
      w.id === id ? { ...w, enabled, layout: { ...w.layout } } : { ...w, layout: { ...w.layout } },
    ),
  };
}

/**
 * Apply `react-grid-layout` layout changes (`x, y, w, h`) back onto `config.widgets`.
 *
 * @process applyLayoutChange
 * Inputs:  config, layout (`RglLayoutItem[]`).
 * Outputs: New `DashboardConfig`.
 */
export function applyLayoutChange(
  config: DashboardConfig,
  layout: RglLayoutItem[],
): DashboardConfig {
  const byId = new Map(layout.map((item) => [item.i, item]));
  return {
    ...config,
    widgets: config.widgets.map((w) => {
      const updated = byId.get(w.id);
      if (!updated) return { ...w, layout: { ...w.layout } };
      return {
        ...w,
        layout: {
          ...w.layout,
          x: updated.x,
          y: updated.y,
          w: updated.w,
          h: updated.h,
        },
      };
    }),
  };
}
